/** Grades as one bar, a segment per grade, sized by how many tickets got it. */

import { cn } from '@/lib/utils';
import { GRADES, gradedCount } from './live-flow';
import type { GradeCounts } from './types';

/** The bar's empty track, which is what ungraded tickets fill. */
const UNGRADED_FILL = 'fill-gray-300';

export const GradeBar = ({ grades, className }: { grades: GradeCounts; className?: string }) => {
	// Ungraded tickets keep their share as the bare track, so the graded segments are never overstated.
	const total = gradedCount(grades) + grades.ungraded;
	const label = GRADES.filter(({ key }) => grades[key] > 0)
		.map(({ grade, key }) => `${grades[key]} ${grade}`)
		.join(', ');
	let offset = 0;
	return (
		<svg
			className={cn('h-1.5 w-full rounded-full', className)}
			viewBox='0 0 100 1'
			preserveAspectRatio='none'
			role='img'
			aria-label={label || 'No graded tickets'}
		>
			<rect width='100' height='1' className={UNGRADED_FILL} />
			{total > 0 &&
				GRADES.map(({ key, className: fill }) => {
					const width = (grades[key] / total) * 100;
					const x = offset;
					offset += width;
					return width > 0 ? <rect key={key} x={x} width={width} height='1' className={fill} /> : null;
				})}
		</svg>
	);
};

/** A grade's colour, drawn with the same fill as its segment of the bar. */
const Swatch = ({ fill }: { fill: string }) => (
	<svg className='size-2 shrink-0' viewBox='0 0 2 2' aria-hidden>
		<circle cx='1' cy='1' r='1' className={fill} />
	</svg>
);

/** The four grades as counts, the good ones first, each keyed to its colour in the bar. */
export const GradeCountsLine = ({ grades }: { grades: GradeCounts }) => (
	<span className='flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-gray-800 tabular-nums'>
		{GRADES.map(({ grade, key, className }) => (
			<span key={key} className='flex items-center gap-1'>
				<Swatch fill={className} />
				{grade} {grades[key]}
			</span>
		))}
		{grades.ungraded > 0 && (
			<span className='flex items-center gap-1'>
				<Swatch fill={UNGRADED_FILL} />
				Not graded {grades.ungraded}
			</span>
		)}
	</span>
);
